import { Injectable } from '@angular/core';
import { Restaurant } from 'src/services/classes/models/restaurant.interface';
import { PersistenceService } from '../persistence/persistence.service';

export interface Credentials {
  dateCreated?: string;
  email?: string;
  id?: number;
  name?: string;
  firstName?: string;
  lastName?: string;
  permissions?: string[];
  phoneNumber?: string;
  pictureUrl?: string;
  role?: string;
  token: string;
  restaurants?: Restaurant[] | [];
}

const credentialsKey = 'credentials';

/**
 * Provides storage for authentication credentials.
 * The Credentials interface should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class CredentialsService {
  private _credentials: Credentials | null = null;

  constructor(private persistenceService: PersistenceService) {
    const savedCredentials = this.persistenceService.get(credentialsKey);
    if (savedCredentials) {
      this._credentials = savedCredentials;
    }
  }

  /**
   * Checks is the user is authenticated.
   * @return True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    return !!this.credentials;
  }

  hasRestaurants(): boolean {
    const restaurants = this.credentials?.restaurants || [];
    if (restaurants.length > 0) {
      if (restaurants.length === 1) {
        const restaurant = restaurants[0];
        if (
          restaurant.status &&
          ['pending', 'declined', 'suspended'].indexOf(
            restaurant.status.toLowerCase()
          ) >= 0
        ) {
          // Adding this here because the dashboard component is never initialised causing
          // the status page not to have access to this data in state
          this.persistenceService.set('current-restaurant', restaurant);
          return false;
        } else {
          return true;
        }
      } else {
        return !!this.credentials && !!this.credentials.restaurants;
      }
    } else {
      return false;
    }
  }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  get credentials(): Credentials | null {
    return this._credentials;
  }

  /**
   * Sets the user credentials.
   * The credentials may be persisted across sessions by setting the `remember` parameter to true.
   * Otherwise, the credentials are only persisted for the current session.
   * @param credentials The user credentials.
   * @param remember True to remember credentials across sessions.
   */
  setCredentials(credentials?: Credentials, remember?: boolean) {
    this._credentials = credentials || null;

    if (credentials) {
      this.persistenceService.set(credentialsKey, credentials);
    } else {
      this.persistenceService.delete(credentialsKey);
    }
  }
}
